import Keycloak from 'keycloak-js'

// Configuración de Keycloak desde variables de entorno
const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'master',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'validacion',
}

console.log('🔧 Configuración Keycloak Back-Panel:', keycloakConfig)

// Instancia de Keycloak (singleton)
let keycloak: Keycloak | null = null

function getKeycloakInstance(): Keycloak {
  if (!keycloak) {
    keycloak = new Keycloak(keycloakConfig)
  }
  return keycloak
}

interface KeycloakUserInfo {
  id: string
  username: string
  email: string
  roles: string[]
  keycloakId: string
  name?: string
}

interface KeycloakTokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

const TOKEN_KEY = 'kc_bp_token'
const REFRESH_TOKEN_KEY = 'kc_bp_refresh_token'

class AuthService {
  private initialized = false
  private initPromise: Promise<boolean> | null = null

  async init(): Promise<boolean> {
    if (this.initialized) {
      console.log('🔐 Keycloak Back-Panel ya inicializado')
      return getKeycloakInstance().authenticated || false
    }
    
    // Si ya hay una inicialización en progreso, esperar a que termine
    if (this.initPromise) {
      console.log('🔐 Esperando inicialización en progreso...')
      return this.initPromise
    }
    
    this.initPromise = this.performInit()
    return this.initPromise
  }

  private saveTokens(): void {
    const kc = getKeycloakInstance()
    if (kc.token) sessionStorage.setItem(TOKEN_KEY, kc.token)
    if (kc.refreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, kc.refreshToken)
  }

  private clearTokens(): void {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  }

  private async performInit(): Promise<boolean> {
    try {
      console.log('🔐 Inicializando Keycloak Back-Panel...')
      
      const kc = getKeycloakInstance()

      const savedToken = sessionStorage.getItem(TOKEN_KEY) ?? undefined
      const savedRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined
      
      const authenticated = await kc.init({
        checkLoginIframe: false,
        pkceMethod: 'S256',
        token: savedToken,
        refreshToken: savedRefreshToken,
      })
      
      console.log('🔐 Keycloak Back-Panel inicializado:', { 
        authenticated,
        token: !!kc.token,
        tokenParsed: !!kc.tokenParsed 
      })
      
      this.initialized = true
      this.initPromise = null
      
      if (authenticated) {
        this.saveTokens()
        this.setupTokenRefresh()
      } else {
        this.clearTokens()
      }
      
      return authenticated
    } catch (error) {
      console.error('❌ Error inicializando Keycloak Back-Panel:', error)
      this.initialized = false
      this.initPromise = null
      throw error
    }
  }

  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Iniciar sesión con redirección a Keycloak
   */
  async login(): Promise<void> {
    if (!this.initialized) {
      await this.init()
    }

    console.log('🔑 Iniciando login Back-Panel con redirección...')
    
    const kc = getKeycloakInstance()
    
    // Redirigir al login de Keycloak con callback específico
    await kc.login({
      redirectUri: window.location.origin + '/auth/callback',
    })
  }

  /**
   * Login legacy para compatibilidad (mantener interfaz)
   */
  async loginLegacy(username: string, password: string): Promise<{ user: KeycloakUserInfo; tokens: KeycloakTokenResponse }> {
    // Redirigir al login moderno
    await this.login()
    throw new Error('Redirección a login moderno')
  }

  /**
   * Cerrar sesión
   */
  async logout(): Promise<void> {
    if (!this.initialized) {
      await this.init()
    }

    console.log('🚪 Cerrando sesión Back-Panel...')
    this.clearTokens()
    
    const kc = getKeycloakInstance()
    
    // Redirigir al logout de Keycloak
    await kc.logout({
      redirectUri: window.location.origin + '/login',
    })
  }

  /**
   * Verificar si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    return getKeycloakInstance().authenticated || false
  }

  /**
   * Obtener el token de acceso
   */
  getToken(): string | undefined {
    return getKeycloakInstance().token
  }

  /**
   * Obtener información del usuario desde el token
   */
  getUserInfo(): KeycloakUserInfo | null {
    const kc = getKeycloakInstance()
    if (!kc.tokenParsed) {
      return null
    }

    return {
      id: kc.tokenParsed.sub || '',
      username: kc.tokenParsed.preferred_username || '',
      name: kc.tokenParsed.name,
      email: kc.tokenParsed.email || '',
      roles: kc.tokenParsed.realm_access?.roles || [],
      keycloakId: kc.tokenParsed.sub || '',
    }
  }

  /**
   * Verificar si el usuario tiene el rol requerido
   */
  hasRole(role: string): boolean {
    const kc = getKeycloakInstance()
    if (!kc.tokenParsed?.realm_access?.roles) {
      return false
    }
    return kc.tokenParsed.realm_access.roles.includes(role)
  }

  /**
   * Verificar si el usuario tiene rol de administrador
   */
  hasAdminAccess(): boolean {
    return this.hasRole('admin')
  }

  /**
   * Actualizar token si es necesario
   */
  async updateToken(minValidity: number = 30): Promise<boolean> {
    if (!this.initialized) {
      return false
    }

    try {
      const kc = getKeycloakInstance()
      const refreshed = await kc.updateToken(minValidity)
      if (refreshed) {
        console.log('🔄 Token Back-Panel renovado exitosamente')
      }
      return refreshed !== false // false = no fue necesario refrescar (token aún válido)
    } catch (error) {
      console.error('❌ Error renovando token Back-Panel:', error)
      return false
    }
  }

  /**
   * Configurar renovación automática de token
   */
  private setupTokenRefresh(): void {
    const kc = getKeycloakInstance()

    // Guardar tokens actualizados tras cada refresh exitoso
    kc.onAuthRefreshSuccess = () => {
      this.saveTokens()
    }

    // Backup: cuando el token expira, refrescar inmediatamente
    kc.onTokenExpired = () => {
      this.updateToken(0).catch(() => {
        console.error('❌ No se pudo renovar el token expirado, cerrando sesión')
        this.clearTokens()
        window.location.href = '/login'
      })
    }

    // Intervalo proactivo: renovar si expira en menos de 60 segundos
    setInterval(async () => {
      try {
        await this.updateToken(60)
      } catch (error) {
        console.error('❌ Error en renovación automática Back-Panel:', error)
        this.clearTokens()
        window.location.href = '/login'
      }
    }, 30 * 1000)
  }

  /**
   * Obtener headers de autorización para requests HTTP
   */
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken()
    if (!token) {
      return {}
    }

    return {
      'Authorization': `Bearer ${token}`,
    }
  }

  /**
   * Obtener instancia de Keycloak (para casos avanzados)
   */
  getKeycloakInstance(): Keycloak {
    return getKeycloakInstance()
  }

  /**
   * Procesar callback de autenticación
   */
  async processAuthenticationCallback(): Promise<boolean> {
    try {
      console.log('🔄 Procesando callback de autenticación...')
      
      // Verificar si estamos en un callback de autenticación
      const urlParams = new URLSearchParams(window.location.search)
      const hasAuthParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('session_state')

      if (!hasAuthParams) {
        console.log('ℹ️ No hay parámetros de callback, verificando autenticación existente')
        return this.isAuthenticated()
      }

      console.log('🔍 Parámetros de callback detectados:', {
        code: urlParams.has('code'),
        state: urlParams.has('state'),
        session_state: urlParams.has('session_state'),
        error: urlParams.has('error')
      })

      // Verificar si hay errores en el callback
      if (urlParams.has('error')) {
        const error = urlParams.get('error')
        const errorDescription = urlParams.get('error_description')
        console.error('❌ Error en callback de Keycloak:', error, errorDescription)
        return false
      }

      // Si Keycloak ya está inicializado, verificar autenticación
      if (this.initialized) {
        console.log('🔐 Keycloak ya inicializado, verificando autenticación...')
        
        if (this.isAuthenticated()) {
          const userInfo = this.getUserInfo()
          console.log('✅ Usuario autenticado:', userInfo?.username, 'Roles:', userInfo?.roles)
          
          // Verificar rol de administrador
          if (!this.hasAdminAccess()) {
            console.error('❌ Usuario sin permisos de administrador. Roles disponibles:', userInfo?.roles)
            return false
          }
          
          console.log('✅ Usuario con permisos de administrador confirmado')
          return true
        }
      }

      // Inicializar Keycloak para procesar el callback
      console.log('🔐 Inicializando Keycloak para procesar callback...')
      
      const kc = getKeycloakInstance()
      
      // Resetear el estado de inicialización para permitir reinicialización
      this.initialized = false
      this.initPromise = null
      
      // Usar init con onLoad para procesar el callback automáticamente
      const authenticated = await kc.init({
        checkLoginIframe: false,
        pkceMethod: 'S256'
      })
      
      this.initialized = true
      console.log('🔐 Keycloak inicializado con callback:', { authenticated })
      
      if (authenticated) {
        this.saveTokens()
        this.setupTokenRefresh()
        
        const userInfo = this.getUserInfo()
        console.log('✅ Usuario autenticado:', userInfo?.username, 'Roles:', userInfo?.roles)
        
        // Verificar rol de administrador
        if (!this.hasAdminAccess()) {
          console.error('❌ Usuario sin permisos de administrador. Roles disponibles:', userInfo?.roles)
          return false
        }
        
        console.log('✅ Usuario con permisos de administrador confirmado')
        return true
      }

      console.log('❌ Autenticación fallida después del callback')
      return false
    } catch (error) {
      console.error('❌ Error procesando callback:', error)
      this.initialized = false
      this.initPromise = null
      return false
    }
  }

  /**
   * Métodos legacy para compatibilidad con store actual
   */
  async refreshToken(refreshToken: string): Promise<KeycloakTokenResponse> {
    const success = await this.updateToken()
    if (!success) {
      throw new Error('Error renovando token')
    }

    const kc = getKeycloakInstance()
    return {
      access_token: kc.token || '',
      refresh_token: kc.refreshToken || '',
      expires_in: kc.tokenParsed?.exp ? kc.tokenParsed.exp - Math.floor(Date.now() / 1000) : 0,
      token_type: 'Bearer'
    }
  }
}

export const authService = new AuthService()
export default authService
